import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { paymentApi } from '../services/api.js';
import Navbar from '../components/Navbar.jsx';

const coreTechnologies = [
  {
    id: 'virtual-try-on',
    name: 'Virtual Try-On for Fashion & Clothing Industry',
    category: 'Fashion & Clothing',
    description:
      'AI fitting room deployment with garment rendering, shopper personalization, and analytics onboarding.',
    highlights: ['3D outfit previews', 'Size confidence scoring', 'Retail analytics dashboard'],
  },
  {
    id: 'pms-hospitality',
    name: 'Property Management System (PMS)',
    category: 'Tourism & Hospitality',
    description:
      'Hospitality operations suite covering reservations, guest workflows, and revenue optimization support.',
    highlights: ['Reservation automation', 'Guest profile intelligence', 'Revenue control workflows'],
  },
  {
    id: 'ecommerce-ai',
    name: 'eCommerce Platform',
    category: 'Consumer Retail',
    description:
      'Commerce acceleration package with personalization, product intelligence, and conversion optimization.',
    highlights: ['Smart recommendations', 'Catalog intelligence', 'Checkout uplift reporting'],
  },
  {
    id: 'ai-research-dev',
    name: 'AI Research & Development',
    category: 'JOFFON R&D',
    description:
      'Advanced AI research engagement for prototyping, model validation, and custom innovation planning.',
    highlights: ['Prototype roadmap', 'Model validation', 'R&D strategy sessions'],
  },
];

const createInvoiceNumber = () => {
  const timestamp = Date.now().toString().slice(-8);
  return `JOF-${timestamp}`;
};

const toPayPalDescription = (value) => value.trim().slice(0, 120) || 'Payment for JOFFON Services';

const recurringOptions = [
  { value: 'ONE_TIME', label: 'One-time payment' },
  { value: 'DAILY', label: 'Daily repeat request' },
  { value: 'WEEKLY', label: 'Weekly repeat request' },
  { value: 'MONTHLY', label: 'Monthly repeat request' },
];

const PayPalBadge = ({ compact = false }) => (
  <div className={`flex items-center ${compact ? 'gap-2' : 'gap-3'}`}>
    <div className={`relative ${compact ? 'h-7 w-8' : 'h-10 w-12'}`} aria-hidden="true">
      <span
        className={`absolute left-0 top-0 flex items-center justify-center rounded-lg bg-[#003087] font-black italic text-white shadow-[0_8px_18px_rgba(0,48,135,0.35)] ${compact ? 'h-7 w-5 text-sm' : 'h-10 w-7 text-xl'}`}
      >
        P
      </span>
      <span
        className={`absolute bottom-0 right-0 flex items-center justify-center rounded-lg bg-[#009cde] font-black italic text-white shadow-[0_8px_18px_rgba(0,156,222,0.35)] ${compact ? 'h-7 w-5 text-sm' : 'h-10 w-7 text-xl'}`}
      >
        P
      </span>
    </div>
    <div className="leading-none">
      <p className={`font-black italic tracking-tight text-white ${compact ? 'text-base' : 'text-xl'}`}>PayPal</p>
      {!compact && <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-400">Trusted checkout</p>}
    </div>
  </div>
);

const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const queryName = decodeURIComponent(queryParams.get('name') || '');
  const queryCategory = decodeURIComponent(queryParams.get('category') || '');
  const queryDescription = decodeURIComponent(queryParams.get('description') || '');
  const queryAmount = Number.parseFloat(queryParams.get('amount') || '');

  const defaultTechnology =
    coreTechnologies.find(
      (technology) => technology.name === queryName || technology.category === queryCategory,
    ) || null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTechnologyId, setSelectedTechnologyId] = useState(defaultTechnology?.id || '');
  const [invoiceNumber] = useState(createInvoiceNumber);
  const [description, setDescription] = useState(queryDescription || '');
  const [recurringPlan, setRecurringPlan] = useState('');
  const [amountInput, setAmountInput] = useState(Number.isFinite(queryAmount) && queryAmount > 0 ? queryAmount.toFixed(2) : '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [step, setStep] = useState(1);

  const selectedTechnology =
    coreTechnologies.find((technology) => technology.id === selectedTechnologyId) || defaultTechnology || {};
  const selectedRecurringOption =
    recurringOptions.find((option) => option.value === recurringPlan) || {};
  const parsedAmount = Number.parseFloat(amountInput);
  const hasValidAmount = Number.isFinite(parsedAmount) && parsedAmount > 0;

  const productName = selectedTechnology.name || 'Custom Payment';
  const productCategory = selectedTechnology.category || 'Uncategorized';

  const handleProceed = () => {
    if (!firstName.trim() || !lastName.trim() || !companyName.trim() || !email.trim()) {
      setError('Please fill in your first name, last name, company name, and email address.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!hasValidAmount) {
      setError('Enter a valid payment amount before continuing.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!recurringPlan) {
      setError('Please select a billing request option.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setError('');
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePayment = async () => {
    if (!hasValidAmount) {
      setError('Enter a valid payment amount before continuing.');
      return;
    }
    if (!recurringPlan) {
      setError('Please select a billing request option.');
      return;
    }

    if (!firstName.trim() || !lastName.trim() || !companyName.trim() || !email.trim()) {
      setError('Please fill in your first name, last name, company name, and email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const orderResponse = await paymentApi.createOrder({
        amount: Number(parsedAmount.toFixed(2)),
        currency: 'USD',
        description: toPayPalDescription(description),
        invoiceNumber,
        customId: `${recurringPlan}|${productCategory}`,
        productName,
        customerInfo: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          companyName: companyName.trim(),
          email: email.trim(),
          phone: phone.trim()
        }
      });

      const { id: orderId, approveLink } = orderResponse.data;

      if (!orderId) {
        throw new Error('Failed to create PayPal order');
      }

      if (!approveLink) {
        throw new Error('PayPal approval link not available');
      }

      window.location.href = approveLink;
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.details || err.message || 'Payment failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#04050c] text-white">
      <Navbar />
      
      <div className="relative overflow-hidden border-b border-cyan-500/20 bg-gradient-to-b from-[#0a0f21] via-[#060713] to-[#04050c]">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[820px] h-[420px] bg-cyan-500/20 blur-[130px] rounded-full pointer-events-none"></div>
        <div className="absolute -top-[180px] -right-[160px] w-[540px] h-[540px] bg-fuchsia-500/20 blur-[140px] rounded-full pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8 relative z-10">
          <button
            onClick={() => navigate('/')}
            className="mb-8 inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </button>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">Secure Payment</h1>
          <p className="text-slate-300/80">Complete your order securely with PayPal</p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
        {error && step === 1 && (
          <div className="mb-8 p-4 bg-red-500/20 border border-red-500 text-red-200 rounded-lg text-sm">
            {error}
          </div>
        )}

        {step === 1 ? (
          <div className="space-y-8">
            <div className="rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-[#0a0f21] to-[#060713] p-8">
              <div className="flex items-center gap-4 mb-8 border-b border-white/10 pb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cyan-500/20 border border-cyan-500/40 shadow-[0_0_15px_rgba(34,211,238,0.2)]">
                  <svg className="w-6 h-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-white tracking-wide">1. Customer Details</h2>
              </div>
              <div className="grid gap-4 md:grid-cols-2 mb-4">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">First Name <span className="text-fuchsia-400">*</span></label>
                  <input type="text" value={firstName} onChange={e => {setFirstName(e.target.value); setError('');}} className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all duration-300 focus:bg-white/10 focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(34,211,238,0.15)]" placeholder="John" />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Last Name <span className="text-fuchsia-400">*</span></label>
                  <input type="text" value={lastName} onChange={e => {setLastName(e.target.value); setError('');}} className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all duration-300 focus:bg-white/10 focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(34,211,238,0.15)]" placeholder="Doe" />
                </div>
              </div>
              <div className="mb-4">
            <label className="block text-slate-300 text-sm font-medium mb-2">Company Name <span className="text-fuchsia-400">*</span></label>
            <input type="text" value={companyName} onChange={e => {setCompanyName(e.target.value); setError('');}} className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all duration-300 focus:bg-white/10 focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(34,211,238,0.15)]" placeholder="JOFFON L.L.C." />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Email Address <span className="text-fuchsia-400">*</span></label>
                  <input type="email" value={email} onChange={e => {setEmail(e.target.value); setError('');}} className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all duration-300 focus:bg-white/10 focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(34,211,238,0.15)]" placeholder="john@example.com" />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Phone Number <span className="text-slate-500 font-normal">(Optional)</span></label>
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all duration-300 focus:bg-white/10 focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(34,211,238,0.15)]" placeholder="+1 (555) 000-0000" />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-fuchsia-500/30 bg-gradient-to-br from-[#0c0817] to-[#060713] p-8">
              <div className="flex items-center gap-4 mb-8 border-b border-white/10 pb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-fuchsia-500/20 border border-fuchsia-500/40 shadow-[0_0_15px_rgba(217,70,239,0.2)]">
                  <svg className="w-6 h-6 text-fuchsia-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-white tracking-wide">2. Payment Details</h2>
              </div>
              <div className="space-y-5">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Amount (USD) <span className="text-fuchsia-400">*</span></label>
                  <div className="relative flex items-center">
                    <span className="absolute left-5 text-2xl font-bold text-fuchsia-400">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={amountInput}
                      onChange={(event) => {
                        setAmountInput(event.target.value);
                        if (error) setError('');
                      }}
                      className="w-full rounded-xl border border-white/10 bg-black/40 pl-11 pr-4 py-4 text-3xl font-bold text-fuchsia-400 outline-none transition-all duration-300 focus:border-fuchsia-400 focus:bg-black/60 focus:shadow-[0_0_20px_rgba(217,70,239,0.15)]"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-400">Type the amount you want to charge in USD.</p>
                </div>
                <div>
                  <label htmlFor="payment-description" className="block text-slate-300 text-sm font-medium mb-2">
                    Description
                  </label>
                  <textarea
                    id="payment-description"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    rows={4}
                    className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all duration-300 focus:bg-white/10 focus:border-fuchsia-400 focus:shadow-[0_0_15px_rgba(217,70,239,0.15)]"
                    placeholder="Enter the service description to appear in PayPal order details"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Repeated billing request</label>
                  <p className="text-xs text-slate-400 mb-3">
                    Select whether this invoice should be marked as daily, weekly, or monthly. PayPal checkout still runs as a single payment.
                  </p>
                  <div className="relative">
                    <select
                      value={recurringPlan}
                      onChange={(event) => setRecurringPlan(event.target.value)}
                      className="w-full appearance-none rounded-xl border border-white/10 bg-[#0d1226] px-4 py-3 text-sm text-white outline-none transition-all duration-300 focus:border-fuchsia-400 focus:shadow-[0_0_15px_rgba(217,70,239,0.15)]"
                    >
                      <option value="" disabled>
                        Select billing request...
                      </option>
                      {recurringOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-400">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-pink-500/30 bg-gradient-to-br from-[#120a16] to-[#060713] p-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 border-b border-white/10 pb-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-pink-500/20 border border-pink-500/40 shadow-[0_0_15px_rgba(236,72,153,0.2)]">
                    <svg className="w-6 h-6 text-pink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white tracking-wide">3. Core Technologies</h2>
                    <p className="text-sm text-slate-400 mt-1">Select the package you want to pay for.</p>
                  </div>
                </div>
                <div className="hidden sm:block rounded-full border border-cyan-500/30 bg-cyan-500/10 px-4 py-2 text-xs uppercase tracking-[0.2em] text-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.2)]">
                  PayPal Ready
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {coreTechnologies.map((technology) => {
                  const isSelected = technology.id === selectedTechnologyId;

                  return (
                    <button
                      key={technology.id}
                      type="button"
                      onClick={() => setSelectedTechnologyId(technology.id)}
                      className={`group rounded-2xl border p-5 text-left transition-all duration-300 ${isSelected ? 'border-pink-400 bg-pink-500/10 shadow-[0_0_22px_rgba(236,72,153,0.18)] -translate-y-1' : 'border-white/10 bg-white/[0.03] hover:border-pink-400/40 hover:bg-pink-500/5 hover:-translate-y-0.5'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className={`text-xs uppercase tracking-[0.2em] mb-2 transition-colors ${isSelected ? 'text-pink-300' : 'text-slate-400 group-hover:text-pink-300/70'}`}>{technology.category}</p>
                          <h3 className="text-lg font-semibold text-white">{technology.name}</h3>
                        </div>
                        <span className={`flex h-6 w-6 items-center justify-center rounded-full border text-sm flex-shrink-0 transition-all ${isSelected ? 'border-pink-300 bg-pink-400/20 text-pink-200 scale-110 shadow-[0_0_10px_rgba(236,72,153,0.4)]' : 'border-slate-600 text-slate-500 group-hover:border-slate-400'}`}>
                          {isSelected ? '✓' : ''}
                        </span>
                      </div>

                      <p className="mt-3 text-sm leading-relaxed text-slate-300">{technology.description}</p>

                      <div className="mt-4 flex flex-wrap gap-2">
                        {technology.highlights.map((highlight) => (
                          <span
                            key={highlight}
                            className={`rounded-full border px-3 py-1 text-xs transition-colors ${isSelected ? 'border-pink-500/30 bg-pink-500/10 text-pink-200' : 'border-white/10 bg-white/5 text-slate-300'}`}
                          >
                            {highlight}
                          </span>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              onClick={handleProceed}
              className="w-full px-6 py-4 bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-xl transition-all duration-300 shadow-[0_0_20px_rgba(34,211,238,0.2)] hover:shadow-[0_0_25px_rgba(34,211,238,0.4)] text-lg"
            >
              Proceed to Order Summary
            </button>
          </div>
        ) : (
          <div className="space-y-8 max-w-2xl mx-auto">
            {error && (
              <div className="p-4 bg-red-500/20 border border-red-500 text-red-200 rounded-lg text-sm">
                {error}
              </div>
            )}
            
            <div className="rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-[#0a0f21] to-[#060713] p-8">
              <h2 className="text-2xl font-bold text-white mb-8 border-b border-white/10 pb-4">Order Summary</h2>
              
              <div className="space-y-6 text-slate-300">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span className="text-slate-400">Invoice Number</span>
                  <span className="font-mono text-cyan-300 bg-cyan-500/10 px-2 py-1 rounded w-fit sm:w-auto">{invoiceNumber}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span className="text-slate-400">Customer</span>
                  <span className="text-white sm:text-right">{firstName} {lastName}<br/><span className="text-sm text-slate-400">{email}</span></span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span className="text-slate-400">Product Selected</span>
                  <span className="text-white sm:text-right sm:max-w-xs">{productName}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span className="text-slate-400">Description</span>
                  <span className="text-white sm:text-right sm:max-w-xs">{description || '--'}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span className="text-slate-400">Billing Request</span>
                  <span className="text-white">{selectedRecurringOption.label || '--'}</span>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center pt-6 border-t border-white/20 mt-4 gap-2">
                  <span className="text-lg font-bold text-white">Total Amount</span>
                  <span className="text-4xl font-bold text-cyan-400">${parsedAmount.toFixed(2)} <span className="text-lg text-slate-400">USD</span></span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={handlePayment}
                disabled={loading}
                className="w-full px-6 py-4 bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:from-cyan-400 hover:to-fuchsia-400 text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_0_20px_rgba(34,211,238,0.4)]"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-3">
                    <PayPalBadge compact />
                    <span className="text-lg">Pay with PayPal</span>
                  </div>
                )}
              </button>

              <button
                onClick={() => { setStep(1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                disabled={loading}
                className="w-full px-6 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-50"
              >
                Back to Edit Details
              </button>
            </div>
            
            <p className="text-xs text-slate-500 text-center">
              Secured by PayPal | SSL Encrypted | USA Payments
            </p>
          </div>
        )}
      </main>

      <footer className="border-t border-fuchsia-500/20 bg-[#04050b] mt-10 py-16">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="text-left">
            <h4 className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 via-fuchsia-200 to-pink-200 font-extrabold tracking-widest mb-2 text-lg">JOFFON L.L.C.</h4>
            <p className="text-slate-400/70 text-sm font-light">3400 Cottage Way, Ste G2 #18859</p>
            <p className="text-slate-400/70 text-sm font-light">Sacramento, California 95825</p>
          </div>
          <div className="text-center md:text-right">
            <p className="text-slate-300/80 text-sm mb-1 font-light">Email: info@joffon.com</p>
            <p className="text-slate-300/80 text-sm mb-6 font-light">Phone: 925-482-6198</p>
            <p className="text-slate-500 text-[10px] uppercase tracking-[0.2em] font-bold">
              &copy; {new Date().getFullYear()} JOFFON AI. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PaymentPage;
